import { FC, PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from 'react';

import { isEqual } from '@eneco/common/date';
import { useDynamicPricing } from '@eneco/hooks/dynamicPricing';
import { useCustomerProfileAccount } from '@eneco/hooks/profile';
import { useMediaQuery } from '@eneco/sparky/hooks';

import { graphProductOptions } from '../utils/constants';
import {
  getDefaultSelectedSection,
  getGraphData,
  getStartDate,
  requestConfigurationMap,
  shiftDate,
  splitDataIntoSections,
} from '../utils/helpers';
import { GraphData, GraphProductOption, GraphViewOption } from '../utils/types';

export type DynamicPricingTariffsContextValue = {
  averagePrice?: number | null;
  data?: GraphData[];
  hasError: boolean;
  isLoading: boolean;
  productTypes: GraphProductOption[] | undefined;
  selectedView: GraphViewOption;
  setSelectedView: (selectedView: GraphViewOption) => void;
  selectedDate?: Date;
  selectedProduct: GraphProductOption | undefined;
  setSelectedProduct: (product: GraphProductOption) => void;
  showTotal: boolean;
  setShowTotal: (showTotal: boolean) => void;
  start: Date | undefined;
  onNextView: () => void;
  onPreviousView: () => void;
};

type DynamicPricingTariffsProviderProps = {
  isRestrictedPage?: boolean;
  hideElectricityOption?: boolean;
  hideGasOption?: boolean;
};

const DynamicPricingTariffsContext = createContext<DynamicPricingTariffsContextValue | null>(null);

export const DynamicPricingTariffsProvider: FC<PropsWithChildren<DynamicPricingTariffsProviderProps>> = ({
  children,
  isRestrictedPage,
  hideElectricityOption,
  hideGasOption,
}) => {
  const isDesktop = useMediaQuery('md');
  const [selectedSection, setSelectedSection] = useState(0);
  const [selectedView, setSelectedView] = useState<GraphViewOption>('day');
  const [selectedDate, setSelectedDate] = useState(getStartDate(selectedView));
  const [queryDate, setQueryDate] = useState(getStartDate(selectedView));
  const [showTotal, setShowTotal] = useState(true);
  const {
    data: products,
    start,
    isLoading,
    hasError,
  } = useDynamicPricing(
    queryDate,
    requestConfigurationMap[selectedView].interval,
    requestConfigurationMap[selectedView].aggregation,
  );
  const { currentAccount } = useCustomerProfileAccount({ shouldFetch: !!isRestrictedPage });

  const [selectedProduct, setSelectedProduct] = useState<
    DynamicPricingTariffsContextValue['selectedProduct'] | undefined
  >(undefined);

  const productTypes = useMemo(
    () =>
      graphProductOptions?.filter(option => {
        if (option === 'electricity' && hideElectricityOption) return false;
        if (option === 'gas' && hideGasOption) return false;

        const isProductTypeAvailable = products?.some(product => product.productType === option);
        const isProductTypeDynamic = currentAccount?.productTypeDetails?.find(
          product => product.productType === option,
        )?.isDynamicPricing;

        return isRestrictedPage ? isProductTypeAvailable && isProductTypeDynamic : isProductTypeAvailable;
      }),
    [currentAccount, hideElectricityOption, hideGasOption, isRestrictedPage, products],
  );

  useEffect(() => {
    if (!selectedProduct && productTypes) {
      setSelectedProduct(productTypes[0]);
    }
  }, [selectedProduct, productTypes]);

  const productData = products?.find(product => product.productType === selectedProduct);

  const formattedData = useMemo(() => {
    const data = getGraphData(selectedView, queryDate, productData?.slices);
    return !isDesktop && selectedView !== 'week' ? splitDataIntoSections(data, selectedView) : [data];
  }, [isDesktop, productData?.slices, selectedView, queryDate]);

  useEffect(() => {
    const queryDate = getStartDate(selectedView);
    setQueryDate(queryDate);

    setSelectedSection(isDesktop ? 0 : getDefaultSelectedSection(formattedData, selectedView));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedView, isDesktop]);

  useEffect(() => {
    const newDate = formattedData[selectedSection]?.[0]?.start;
    if (!isEqual(newDate, selectedDate)) setSelectedDate(newDate);
  }, [formattedData, selectedDate, selectedSection]);

  const onNextView = () => {
    if (selectedSection < formattedData.length - 1) {
      setSelectedSection(value => value + 1);
    } else {
      setSelectedSection(0);
      setQueryDate(shiftDate(selectedView, queryDate, 1));
    }
  };

  const onPreviousView = () => {
    if (selectedSection > 0) {
      setSelectedSection(value => value - 1);
    } else {
      setSelectedSection(formattedData.length - 1);
      setQueryDate(shiftDate(selectedView, queryDate, -1));
    }
  };

  const averagePrice = showTotal ? productData?.averagePriceVat?.inclVat : undefined;

  const contextValue = useMemo(
    () => ({
      averagePrice,
      data: formattedData?.[selectedSection],
      hasError,
      isLoading,
      productTypes,
      selectedView,
      setSelectedView,
      selectedDate,
      selectedProduct,
      setSelectedProduct,
      showTotal,
      setShowTotal,
      start,
      onNextView,
      onPreviousView,
    }),
    [
      averagePrice,
      formattedData,
      selectedSection,
      hasError,
      isLoading,
      productTypes,
      selectedView,
      selectedDate,
      selectedProduct,
      showTotal,
      start,
      onNextView,
      onPreviousView,
    ],
  );

  return (
    <DynamicPricingTariffsContext.Provider value={contextValue}>{children}</DynamicPricingTariffsContext.Provider>
  );
};

export const useDynamicPricingTariffsOptional = () => {
  return useContext(DynamicPricingTariffsContext);
};

export const useDynamicPricingTariffs = () => {
  const context = useDynamicPricingTariffsOptional();

  if (!context) throw new Error('useDynamicPricingTariffs must be wrapped inside DynamicPricingTariffsProvider');
  return context;
};
